import React from "react";
import { useTranslation } from "react-i18next";
import Moment from "moment";
import { TextField, Tooltip, Typography } from "@material-ui/core";
import clsx from "clsx";
import { ILogDictionaryInputConfig, ILogEnvironmentInputConfig, IRoleInputConfig, ITextBoxInputConfig } from "../RelationAccesses/IModels";
import RequiredIcon from "./RequiredIcon";
import lang from './RelationsLang';
import QdDatePickerControl from "../Datetime/QdDatePickerControl";
import { IDateInputConfig, ITextAreaInputConfig } from "./Models";
import RolePermissionsMultiAutocomplete from "../RelationAccesses/RolePermissionsControl";
import { IModuleEntity, IRolePerrmission } from "../RelationAccesses/AccessDomainModels";
import RolePermissionsAutocomplete from "../RelationAccesses/RolePermissionsAutocomplete";
import { createStyles, makeStyles } from "@material-ui/styles";
import LogDictionaryAutocomplete from "../LogDictionaryAutocomplete";
import LogCompanyAutocomplete from "../LogCompanyAutocomplete";
import LogEnvironmentAutocomplete from "../LogEnvironmentAutocomplete";
import { OptionType } from "../ComboboxMI";

const useStyles = makeStyles((theme) =>
	createStyles({
		label: {
			color: '#888',
			display: 'block',
			marginTop: '7px',
		},
		textInput: {
			'& input[type="text"]': {
				padding: '9px',
			}
		}, 
		passwordInput: {
			'& input[type="password"]': {
				padding: '9px',
			}
		}
	}),
);

interface IStaticLabelFormRowProps {
	label: string,
	required: boolean,
}

export function StaticLabelFormRow(props: IStaticLabelFormRowProps) {
	const classes = useStyles();
	return <label className={classes.label} >
		{props.label}
		<RequiredIcon required={props.required} />:
	</label>
}

interface ILabelFormRowProps {
	name?: string,
	nameFn?: () => string,

	config: {
		required: boolean,

		label?: string,
		labelPl?: string,
		labelEn?: string,
	},
}

export function LabelFormRow(props: ILabelFormRowProps) {
	const classes = useStyles();
	return <label className={classes.label} >
		{props.name && lang.getDefaultByPrefix(props.config, "label", props.name)}
		{props.nameFn && lang.getDefaultByPrefixFromFn(props.config, "label", props.nameFn)}
		<RequiredIcon required={props.config.required} />:
	</label>
}

interface ITextareaFormRowProps {
	name: string,
	errorText: string,
	config: ITextAreaInputConfig;

	value: string,
	onChange: (v: string) => void
}

export function TextareaFormRow(props: ITextareaFormRowProps) {
	return <>
		{props.errorText && <p className="text-danger">{props.errorText}</p>}
		{!props.config.hidden && <div className="text-field">
			<LabelFormRow
				{...props}
			/>
			<TextField
				multiline
				rows={4}
				value={props.value}
				onChange={(e) => props.onChange(e.target.value)}
				className={clsx("textarea-input")}
				error={!!props.errorText}
				placeholder={lang.getDefaultByPrefix(props.config, "placeholder")}

				variant="outlined"
				fullWidth
			/>
		</div>}
	</>
}

interface ITextFieldFormRowProps {
	name: string,
	errorText: string,
	config: ITextBoxInputConfig;

	value: string,
	onChange: (v: string) => void
}

export function TextFieldFormRow(props: ITextFieldFormRowProps) {
	const classes = useStyles();

	return <>
		{props.errorText && <p className="text-danger">{props.errorText}</p>}
		{!props.config.hidden && <div className="text-field">
			<LabelFormRow
				{...props}
			/>
			<TextField
				InputProps={{
					className: classes.textInput,
				}}
				value={props.value}
				onChange={(e) => props.onChange(e.target.value)}
				className={clsx("text-input")}
				error={!!props.errorText}
				placeholder={lang.getDefaultByPrefix(props.config, "placeholder")}

				variant="outlined"
				fullWidth
			/>
		</div>}
	</>
}

interface IPasswordsFieldFormRowProps {
	name: string,
	errorText: string,
	config: ITextBoxInputConfig;

	value1: string,
	value2: string,
	onChange: (v1: string, v2: string) => void
}

export function PasswordsFieldFormRow(props: IPasswordsFieldFormRowProps) {
	const classes = useStyles();

	return <div>
		{props.errorText && <p className="text-danger">{props.errorText}</p>}
		{!props.config.hidden && <form autoComplete="off" >
			<div className="text-field">
				<LabelFormRow
					{...props}
				/>
				<div>
					<TextField
						InputProps={{
							className: classes.passwordInput,
						}}
						type="password"
						value={props.value1}
						onChange={(e) => props.onChange(e.target.value, props.value2)}
						error={!!props.errorText}
						className={clsx("text-input")}
						placeholder={lang.getDefaultByPrefix(props.config, "placeholder")}

						variant="outlined"
						fullWidth
					/>
				</div>
				<div>
					<StaticLabelFormRow
						required={props.config.required}
						label={lang.get("rePassword")}
					/>
					<TextField
						InputProps={{
							className: classes.passwordInput,
						}}
						type="password"
						value={props.value2}
						onChange={(e) => props.onChange(props.value1, e.target.value)}
						error={!!props.errorText}
						variant="outlined"
						fullWidth
					/>
				</div>
			</div>
		</form>}
	</div>
}

interface IDatesFieldFormRowProps {
	editRow: boolean,

	errorText: string,

	dateFromConfig: IDateInputConfig;
	dateToConfig: IDateInputConfig;

	from: Moment.Moment,
	to: Moment.Moment,

	onChange: (from: Moment.Moment, to: Moment.Moment) => void
}

export function DatesFieldFormRow(props: IDatesFieldFormRowProps) {
	const { t } = useTranslation();
	return <div className="dates-inline">
		{props.errorText && <p className="text-danger">{t(props.errorText)}</p>}

		{!props.dateFromConfig.hidden && <div className="form-field date-inline">
			<LabelFormRow
				config={props.dateFromConfig}
				name="from"
			/>

			{!props.editRow && <QdDatePickerControl
				value={props.from}
				onChange={(x) => props.onChange(x, props.to)}
			/>}
			{props.editRow && <div>{props.from?.format("L")}</div>}
		</div>}

		{!props.dateToConfig.hidden && <div className="form-field date-inline">
			<LabelFormRow
				config={props.dateToConfig}
				name="to"
			/>
			<QdDatePickerControl
				value={props.to}
				onChange={x => props.onChange(props.from, x)}
				className={clsx({
					"invalid-value": props.errorText,
				})}
			/>
		</div>}
	</div>
}

interface IRolePermissionsMultiFormRowProps {
	config: IRoleInputConfig,
	isApplication: boolean,
	errorText: string,

	value: IRolePerrmission[],
	onSelect: (v: IRolePerrmission[]) => void,
	applicationId: number,
}

export function RolePermissionsMultiFormRow(props: IRolePermissionsMultiFormRowProps) {

	if (props.config.hidden)
		return null;

	if (!props.isApplication)
		return null;

	let maxRows = props.config.maxRows;
	if (!maxRows) {
		maxRows = 20;
	}
	return <>
		{props.errorText && <p className="text-danger">{props.errorText}</p>}
		<div className="text-field">
			<LabelFormRow
				name="selectRolePermissions"
				config={props.config}
			/>
			<RolePermissionsMultiAutocomplete
				applicationId={props.applicationId}
				take={maxRows}
				value={props.value}
				onSelect={props.onSelect}
				placeholder={lang.getDefaultByPrefix(props.config, "placeholder")}
				administrativeRoleText={lang.administrativeRole()}
				sensitiveDataText={lang.sensitiveData()}
			/>
		</div>
	</>
}

interface IRolePermissionSingleFormRowProps {
	config: IRoleInputConfig,
	isApplication: boolean,
	errorText: string,

	value: IRolePerrmission,
	onSelect: (v: IRolePerrmission) => void,

	applicationId: number,
	role: IRolePerrmission,
}

export function RolePermissionSingleFormRow(props: IRolePermissionSingleFormRowProps) {

	if (props.config.hidden)
		return null;

	if (!props.isApplication)
		return null;

	let permissions: IRolePerrmission[] = [];
	if (props.role && (!props.role.id || props.role.id == -1)) {
		permissions.push({
			id: -1,
			name: props.role.name,
			description: "",
			administrativeRights: false,
			excludedRolePermissions: [],
			reasonForProcessingSensitiveData: "",
			sensitiveData: false,
		});
	}

	let info: any = null;
	if (props.value) {
		let role = props.value;
		if (role && (role.administrativeRights || role.description)) {
			info = (<Tooltip title={<React.Fragment>
				<Typography style={{ whiteSpace: "pre-line" }}>{role.description}</Typography>
				{role.administrativeRights && <p>({lang.administrativeRole()})</p>}
				</React.Fragment>}
			>
				<i className="material-icons text-info" style={{ verticalAlign: "middle", cursor: "pointer" }} >info</i>
			</Tooltip>);
		}
	}

	return <>
		{props.errorText && <p className="text-danger">{props.errorText}</p>}
		<div className="text-field">
			<LabelFormRow
				name="selectRolePermissions"
				config={props.config}
			/>
			<div className="permission-role-single-control">
				<RolePermissionsAutocomplete
					additionalValues={permissions}
					applicationId={props.applicationId}
					take={props.config.maxRows}
					value={props.value}
					onSelect={props.onSelect}
					placeholder={lang.getDefaultByPrefix(props.config, "placeholder")}
					administrativeRoleText={lang.administrativeRole()}
					sensitiveDataText={lang.sensitiveData()}
				/>
				{info}
			</div>
		</div>
	</>
}

interface ILogDictionaryFormRowProps {
	name: string,
	errorText: string,
	config: ILogDictionaryInputConfig;
	dictionaryType: string,
	value: string,
	onChange: (v: string) => void
}

export function LogDictionaryFormRow(props: ILogDictionaryFormRowProps) {
	return <>
		{props.errorText && <p className="text-danger">{props.errorText}</p>}
		{!props.config.hidden && <div className="text-field">
			<LabelFormRow
				{...props}
			/>
			{props.dictionaryType == "Company" && <LogCompanyAutocomplete
				value={props.value}
				onChange={props.onChange}
				className={clsx({
					"invalid-value": props.errorText,
				})}
				errorText={props.errorText}
				isDemo={false}
				isLogSystemIntegrationAvailable={true}
			/>}
			{props.dictionaryType != "Company" && <LogDictionaryAutocomplete
				value={props.value}
				onChange={props.onChange}
				dictionaryType={props.dictionaryType}
				className={clsx({
					"invalid-value": props.errorText,
				})}
				errorText={props.errorText}
				isDemo={false}
				isLogSystemIntegrationAvailable={true}
			/>}
		</div>}
	</>
}

interface ILogEnvironmentFormRowProps {
	name: string,
	errorText: string,
	config: ILogEnvironmentInputConfig;
	value: string,
	onChange: (v: string) => void,

	application?: IModuleEntity,

	selectedResource?: OptionType,
	selectedResourceKind?: "Application" | "ServiceAsset"
}

export function LogEnvironmentFormRow(props: ILogEnvironmentFormRowProps) {
	const classes = useStyles();
	let applicationId: number | null = null;
	if (props.config.type === 'autocomplete') {
		if (props.application && props.application.kind === 'Application' )
			applicationId = props.application.id;
		if (props.selectedResource && props.selectedResourceKind === 'Application') {
			applicationId = parseInt(props.selectedResource.value, 10);
		}
	}
	return <>
		{props.errorText && <p className="text-danger">{props.errorText}</p>}
		{!props.config.hidden && <div className="text-field">
			<LabelFormRow
				{...props}
			/>
			{applicationId && <LogEnvironmentAutocomplete
				value={props.value}
				applicationId={applicationId}
				onChange={props.onChange}
				className={clsx({
					"invalid-value": props.errorText,
				})}
				errorText={props.errorText}
				isDemo={false}
				isLogSystemIntegrationAvailable={true}
			/>}
			{!applicationId &&
				<TextField
					InputProps={{
						className: classes.textInput,
					}}
					value={props.value}
					onChange={(e) => props.onChange(e.target.value)}
					className={clsx("text-input")}
					error={!!props.errorText}
					placeholder={lang.getDefaultByPrefix(props.config, "placeholder")}

					variant="outlined"
					fullWidth
				/>}
		</div>}
	</>
}